import { FILTER_INPUTS_REGISTRY } from '@aibees/elephant/feature/search-filters/constants';
import {
  AutocompleteOption,
  AutocompleteOptionType,
  Classification,
  LocatioData,
  SearchFilter,
  SearchFilterBase,
  SearchFilterBaseSections,
  SearchFilterConfig,
} from '@aibees/elephant/feature/search-filters/types';
import { ExportItem, ExportSearchGrouping } from '@aibees/elephant/hooks';
import { PeopleMetadataItem } from '@aibees/elephant/hooks/people/types';
import type {
  CompanyMetadataItem,
  School,
  SearchBrowseParams,
} from '@aibees/elephant/types';
import { IntegrationType } from '@aibees/elephant/types/integration';
import {
  Label,
  LabelGrouping,
  LabelType,
  LabelVisibility,
  OwnershipScope,
} from '@aibees/elephant/types/label';
import { IconType } from '@aibees/shared/components';
import { Enrichment, MemberStatus, Workspace } from '@aibees/shared/definition';
import { DATE_FORMAT_MMM_DD_YYYY, fDateTime } from '@aibees/shared/utils';
import { isEmpty, uniqBy } from 'lodash-es';
import { createElement, ReactNode } from 'react';

export function convertDataToKeyValueUseConfig(
  data: Record<string, any>[],
  config: SearchFilterConfig,
  keepSelectedOptions?: boolean,
  addSelectAllOption?: boolean,
  valueFormatter?: (...args: any) => string,
  delimiter?: string
): AutocompleteOption[] {
  let aggregated: Record<string, any>[] = [];

  if (data) {
    if (addSelectAllOption)
      aggregated = [
        {
          [config.labelKey]: 'Select All',
          [config.valueKey]: AutocompleteOptionType.SELECT_ALL,
          group: AutocompleteOptionType.SELECT_ALL,
          groupType: AutocompleteOptionType.SELECT_ALL,
        },
      ];
    if (keepSelectedOptions) aggregated = [...aggregated, ...data];
    else aggregated = [...data];
  }

  return aggregated.map((item) => {
    const value = item[config.valueKey];
    const label = item[config.labelKey] ?? item[config.valueKey];
    return {
      group: item.group ?? item.groupType,
      label,
      value:
        valueFormatter && delimiter
          ? String(valueFormatter?.(value, delimiter, item))
          : String(value),
      metadata: {
        ...item.metadata,
        domain: item.domain,
        records: item.records,
        category: item.group,
        id: item.id,
        url: item.url,
        iconSubtitle: item.iconSubtitle,
        subtitle: item.subtitle,
        grouping: item.grouping,
      },
    };
  });
}

/**
 * Resolve a filter's stable persistence identity for pin/order presets.
 * Falls back to `name` when `id` is not set, so filters that have never been
 * renamed keep working with no config change. See `SearchFilterBase.id`.
 */
export function getFilterId(filter: Pick<SearchFilterBase, 'id' | 'name'>) {
  return filter.id ?? filter.name;
}

export function isDependentSearchFilter(
  metadata: SearchFilter['metadata'],
  params: Partial<SearchBrowseParams>
) {
  return !!(
    metadata?.availability?.requires?.length &&
    metadata.availability?.requires.every((x) => isEmpty(params[x]))
  );
}

export function renderFilterComponent(
  item: SearchFilter,
  open: boolean,
  sections?: SearchFilterBaseSections,
  children?: ReactNode[],
  props?: Record<string, any>
) {
  const component = FILTER_INPUTS_REGISTRY.get(item.type) ?? null;
  const { sections: itemSections, ...other } = item;

  return (
    component &&
    createElement(
      component,
      {
        ...other,
        open,
        sections: itemSections ?? sections,
        ...{
          key: item.field?.length ? item.field : `${item.type}-${item.index}`,
        },
        ...props,
      },
      children
    )
  );
}

export function findOpenFilterItems(filter?: SearchFilter) {
  if (!filter) return [];
  return (
    (filter.items
      ?.filter((item) => !!item.open)
      .map((item) => item.field) as string[]) ?? []
  );
}

/**
 * Parse a 2-part `name::id` URL value where the name itself may contain `:` or `::`.
 * Right-greedy: name = everything before the LAST delimiter, id = everything after.
 *
 * For 3-part values (`name::id::title`, `name::id::domain`), use
 * parseNameIdExtraValue instead.
 */
export function parseNameIdValue(
  value: string,
  delimiter = '::'
): { name: string; id: string } {
  const i = value.lastIndexOf(delimiter);
  if (i === -1) return { name: value, id: '' };
  return {
    name: value.slice(0, i),
    id: value.slice(i + delimiter.length),
  };
}

const UUID_PATTERN =
  /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;

/**
 * Parse a 3-part `name::id::extra` URL value (e.g. `name::id::title` from
 * peopleSearchApiResponseMapper, `name::id::domain` from listsResponseSchoolMapper).
 *
 * Anchors on the UUID id format so the parse stays correct when name OR extra
 * contains `::`. Scans the whole string (not segment-by-segment) so names
 * ending or starting with an odd number of `:` characters parse correctly —
 * pre-splitting on `::` leaves a stray colon glued to the UUID segment and
 * the pattern check fails. Picks the LAST `::<UUID>::` match so a UUID-shaped
 * substring embedded in the name doesn't win over the actual id.
 *
 * Falls back to positional split[0]/[1] if no delimiter-bounded UUID is found
 * — preserves prior behavior on bad data instead of throwing.
 */
export function parseNameIdExtraValue(
  value: string,
  delimiter = '::'
): { name: string; id: string; extra: string } {
  let last: { idx: number; uuid: string } | null = null;
  for (const m of Array.from(value.matchAll(UUID_PATTERN))) {
    const idx = m.index;
    if (idx === undefined) continue;
    const uuid = m[0];
    const beforeOk =
      idx >= delimiter.length &&
      value.slice(idx - delimiter.length, idx) === delimiter;
    const afterOk =
      value.slice(idx + uuid.length, idx + uuid.length + delimiter.length) ===
      delimiter;
    if (beforeOk && afterOk) last = { idx, uuid };
  }
  if (!last) {
    const segments = value.split(delimiter);
    return {
      name: segments[0] ?? '',
      id: segments[1] ?? '',
      extra: segments.slice(2).join(delimiter),
    };
  }
  return {
    name: value.slice(0, last.idx - delimiter.length),
    id: last.uuid,
    extra: value.slice(last.idx + last.uuid.length + delimiter.length),
  };
}

export const listsResponseMapper = (
  data?: Label[],
  icon?: IconType,
  delimiter?: string
) => {
  if (!data) return [];
  return data.map((item) => {
    return {
      ...item,
      ...{
        value: `${item.name}${delimiter}${item.id}`,
        metadata: {
          icon,
          secondaryIcon:
            item.visibility === LabelVisibility.PRIVATE
              ? 'eye-lock-outline'
              : undefined,
          secondaryIconColor: 'grey.700',
          secondaryIconSize: 16,
        },
        records: item.records ?? 0,
      },
    };
  });
};

export const listAndLabelResponseFilterFn = (
  data: Label,
  type: LabelType,
  grouping: LabelGrouping,
  ownershipScope?: OwnershipScope,
  userId?: string
) => {
  if (ownershipScope === OwnershipScope.MINE) {
    return (
      data.type === type &&
      data.grouping === grouping &&
      data.workspace.user.id === userId
    );
  } else if (ownershipScope === OwnershipScope.TEAM) {
    return (
      data.type === type &&
      data.grouping === grouping &&
      data.workspace.user.id !== userId
    );
  }
  return data.type === type && data.grouping === grouping;
};

export const listsResponseSchoolMapper = (
  data?: School[],
  icon?: IconType,
  delimiter?: string
) => {
  if (!data) return [];
  return data.map((item) => {
    return {
      ...item,
      ...{
        label: item.name,
        value: `${item.name}${delimiter}${item.id}${delimiter}${item.domain}`,
        subtitle: 'School',
        metadata: {
          logo_url: item.logo ?? 'no logo found',
        },
      },
    };
  });
};

export const exportDataRequestApiMapper = (data?: ExportItem[]) => {
  if (!data) return [];
  return data.map((item) => {
    const date = fDateTime(item.created, DATE_FORMAT_MMM_DD_YYYY);

    let logoUrl = '/assets/images/ark-logo.svg';
    if (item.grouping === ExportSearchGrouping.PERSON) {
      const hasSmartleadIntegration =
        !!item.integrations?.[IntegrationType.SMART_LEAD];
      logoUrl = hasSmartleadIntegration
        ? '/assets/images/Smartlead.jpg'
        : '/assets/images/ark-logo.svg';
    }

    return {
      ...item,
      subtitle: `${item.workspace.user.firstName} - ${date}`,
      // iconSubtitle: 'person-outline',
      metadata: {
        logo_url: logoUrl,
      },
    };
  });
};

export const enrichmentDataRequestApiMapper = (data?: Enrichment[]) => {
  if (!data) return [];
  return data.map((item) => {
    const date = fDateTime(item.created, DATE_FORMAT_MMM_DD_YYYY);

    return {
      ...item,
      subtitle: `${item.workspace.user.firstName} - ${date}`,
    };
  });
};

export const technologyApiResponseMapper = (data?: Classification[]) => {
  if (!data) return [];
  return data.map((item) => {
    return {
      ...item,
      subtitle: item.metadata?.category,
    };
  });
};

export const workspaceResponseMapper = (data?: Workspace[]) => {
  if (!data || !data?.[0]) return [];
  const members = uniqBy(
    [
      ...[data[0].user],
      ...data[0].members
        .filter((m) => m.status === MemberStatus.Active)
        .map((m) => m.user),
    ],
    'id'
  );
  return members
    .filter((x) => !!x)
    .map((item) => {
      return {
        name: `${item.firstName} ${item.lastName}`,
        id: item.id,
        // TODO: calculate the records after the data provided by the back-end (Also we need the bootstrap data here for calculation)
        records: 0,
      };
    });
};

export const locationsApiResponseMapper = (data?: LocatioData[]) => {
  if (!data) return [];
  return data.map((item) => {
    return {
      value: item.rawAddress,
      label: item.rawAddress,
    };
  });
};

export const companySearchApiResponseMapper = (
  data?: CompanyMetadataItem[],
  filterIcon?: IconType,
  delimiter?: string
) => {
  if (!data) return [];
  return data.map((item) => {
    return {
      value: `${item.name}${delimiter}${item.id}`,
      label: item.name,
      subtitle: item.url,
      metadata: {
        logo_url: item.logo ?? 'no logo found',
      },
    };
  });
};

export const peopleSearchApiResponseMapper = (
  data?: PeopleMetadataItem[],
  filterIcon?: IconType,
  delimiter?: string
) => {
  if (!data) return [];
  return data.map((item) => {
    return {
      value: `${item.name}${delimiter}${item.id}${delimiter}${item.title}`,
      label: item.name,
      subtitle: item.title,
      metadata: {
        logo_url: item.logo ?? 'no logo found',
      },
    };
  });
};

export function useOptionsUrl(
  options: SearchFilter['options'],
  activeTabIndex: number,
  tabs?: string[]
) {
  let url = options?.find((o) => o.type === 'URL')?.value;
  if (tabs?.length) {
    url = options?.[activeTabIndex]?.value;
  } else {
    url = options?.find((o) => o.type === 'URL')?.value;
  }
  return url;
}
